import type { BooruPost } from "./booru-api"

/**
 * Content Type Detector
 * 
 * Determines if content should allow Collection Modal display based on content type.
 * Categories and sub-categories should NOT show the collection modal.
 * Covers (books/doujins) and individual media items SHOULD show the collection modal.
 */

export class ContentTypeDetector {
  /**
   * Determines if the Collection Modal should be shown for this content
   * @param post - The BooruPost to check
   * @returns true if Collection Modal should be shown, false otherwise
   */
  shouldShowCollectionModal(post: BooruPost): boolean {
    try {
      // Categories and sub-categories should NOT show collection modal
      if (this.isCategory(post) || this.isSubCategory(post)) {
        return false
      }
      
      // Covers and individual media items SHOULD show collection modal
      return true
    } catch (error) {
      console.warn('[ContentTypeDetector] Detection failed:', error)
      // Fail-safe: default to allowing Collection Modal
      return true
    }
  }

  /**
   * Checks if a post is a category page
   * @param post - The BooruPost to check
   * @returns true if post is a category
   */
  isCategory(post: BooruPost): boolean {
    // Check explicit category flag
    if (post.rawPost?.isCategory === true) {
      return true
    }

    // Heuristic: Check if galleryUrl contains "category:"
    if (post.rawPost?.galleryUrl && typeof post.rawPost.galleryUrl === 'string') {
      if (post.rawPost.galleryUrl.includes('category:')) {
        return true
      }
    }

    return false
  }

  /**
   * Checks if a post is a sub-category page
   * @param post - The BooruPost to check
   * @returns true if post is a sub-category
   */
  isSubCategory(post: BooruPost): boolean {
    // Check explicit sub-category flag
    if (post.rawPost?.isSubCategory === true) {
      return true
    }

    // Heuristic: Check if galleryUrl contains "subcategory:" or similar patterns
    if (post.rawPost?.galleryUrl && typeof post.rawPost.galleryUrl === 'string') {
      const url = post.rawPost.galleryUrl.toLowerCase()
      if (url.includes('subcategory:') || url.includes('sub-category:')) {
        return true
      }
    }

    return false
  }

  /**
   * Checks if a post is a cover (book/doujin)
   * @param post - The BooruPost to check
   * @returns true if post is a cover
   */
  isCover(post: BooruPost): boolean {
    // Check if post has album images (indicates it's a cover with pages)
    if (post.albumImages && Array.isArray(post.albumImages) && post.albumImages.length > 0) {
      return true
    }

    // Check if post has albumMediaCount (indicates it's a cover)
    if (post.albumMediaCount && post.albumMediaCount > 0) {
      return true
    }

    // Check if type is explicitly set to "album"
    if (post.type === 'album') {
      return true
    }

    return false
  }

  /**
   * Checks if a post is an individual media item (image or video)
   * @param post - The BooruPost to check
   * @returns true if post is an individual media item
   */
  isMediaItem(post: BooruPost): boolean {
    // If it's not a category, sub-category, or cover, it's a media item
    return !this.isCategory(post) && !this.isSubCategory(post) && !this.isCover(post)
  }
}

// Export singleton instance for convenience
export const contentTypeDetector = new ContentTypeDetector()
